import type { FormValues } from "@/hooks/useCreateLead";
import { getLeadEmailFieldError } from "@/lib/utils";

export type LeadPopup7DynamicFieldKind =
  | "text"
  | "email"
  | "select"
  | "qualification"
  | "course";

export type LeadPopup7DynamicFieldLeadKey =
  | "name"
  | "first_name"
  | "last_name"
  | "email"
  | "qualification"
  | "course"
  | "interest"
  | "c2"
  | "c3"
  | "c4"
  | "c5";

export type LeadPopup7DynamicFieldOption = {
  value: string;
  label: string;
};

export type LeadPopup7DynamicField = {
  id: string;
  kind: LeadPopup7DynamicFieldKind;
  /** Shown above control */
  label?: string;
  placeholder?: string;
  required?: boolean;
  /** For kind "select" only */
  options?: LeadPopup7DynamicFieldOption[];
  /**
   * Where the value lands on CreateLead payloads.
   * Use c2–c5 for free-form segmentation without API schema changes.
   */
  bind?: LeadPopup7DynamicFieldLeadKey;
};

const LEAD_SCALAR_KEYS = new Set<LeadPopup7DynamicFieldLeadKey>([
  "name",
  "email",
  "qualification",
  "course",
  "interest",
]);

export function parseDynamicFieldDefinitions(
  raw: unknown,
): LeadPopup7DynamicField[] {
  if (raw == null || raw === "") return [];

  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw.trim() || "[]") as unknown;
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  const out: LeadPopup7DynamicField[] = [];
  for (const item of parsed) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const id = typeof o.id === "string" ? o.id.trim() : "";
    const kindRaw = typeof o.kind === "string" ? o.kind.trim() : "text";
    if (!id) continue;

    const kind = (
      ["text", "email", "select", "qualification", "course"].includes(kindRaw)
        ? kindRaw
        : "text"
    ) as LeadPopup7DynamicFieldKind;

    const bindRaw = typeof o.bind === "string" ? o.bind.trim() : "";
    const bind = (
      [
        "name",
        "first_name",
        "last_name",
        "email",
        "qualification",
        "course",
        "interest",
        "c2",
        "c3",
        "c4",
        "c5",
      ].includes(bindRaw)
        ? bindRaw
        : undefined
    ) as LeadPopup7DynamicFieldLeadKey | undefined;

    let options: LeadPopup7DynamicFieldOption[] | undefined;
    if (Array.isArray(o.options)) {
      options = o.options
        .map((op) => {
          if (!op || typeof op !== "object") return null;
          const r = op as Record<string, unknown>;
          const value = typeof r.value === "string" ? r.value : "";
          const label = typeof r.label === "string" ? r.label : value;
          if (!value) return null;
          return { value, label };
        })
        .filter(Boolean) as LeadPopup7DynamicFieldOption[];
    }

    out.push({
      id,
      kind,
      label: typeof o.label === "string" ? o.label : undefined,
      placeholder: typeof o.placeholder === "string" ? o.placeholder : undefined,
      required: Boolean(o.required),
      options,
      bind,
    });
  }

  return out;
}

export function validateDynamicFields(
  defs: LeadPopup7DynamicField[],
  dynamic: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const d of defs) {
    if (!d.required) continue;
    const v = (dynamic[d.id] ?? "").trim();
    if (!v) {
      errors[d.id] = "This field is required";
      continue;
    }
    if (d.kind === "email" || d.bind === "email") {
      const err = getLeadEmailFieldError(v);
      if (err) errors[d.id] = err;
    }
  }
  return errors;
}

/** Maps an existing CRM lead onto dynamic ids using `bind` keys. */
export function seedDynamicFromLead(
  lead: Record<string, unknown> | null | undefined,
  defs: LeadPopup7DynamicField[],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!lead || typeof lead !== "object") return out;
  for (const d of defs) {
    if (!d.bind) continue;
    let v = "";
    if (d.bind === "name") v = String((lead as { name?: unknown }).name ?? "");
    else if (d.bind === "email")
      v = String((lead as { email?: unknown }).email ?? "");
    else if (d.bind === "first_name") {
      const full = String((lead as { name?: unknown }).name ?? "").trim();
      const parts = full.split(/\s+/).filter(Boolean);
      v = parts[0] ?? "";
    } else if (d.bind === "last_name") {
      const full = String((lead as { name?: unknown }).name ?? "").trim();
      const parts = full.split(/\s+/).filter(Boolean);
      v = parts.slice(1).join(" ") || "";
    } else if (d.bind === "qualification")
      v = String((lead as { highest_qualification?: unknown }).highest_qualification ?? "");
    else if (d.bind === "course" || d.bind === "interest") {
      v = String((lead as { area_of_interest?: unknown }).area_of_interest ?? "");
      if (!v && d.bind === "interest")
        v = String((lead as { interest?: unknown }).interest ?? "");
    } else if (d.bind === "c2") v = String((lead as { c2?: unknown }).c2 ?? "");
    else if (d.bind === "c3") v = String((lead as { c3?: unknown }).c3 ?? "");
    else if (d.bind === "c4") v = String((lead as { c4?: unknown }).c4 ?? "");
    else if (d.bind === "c5") v = String((lead as { c5?: unknown }).c5 ?? "");
    if (v) out[d.id] = v;
  }
  return out;
}

/** Applies dynamic captures onto the flat lead form payload. */
export function mergeLeadPopup7Dynamics(
  formValues: Record<string, any>,
  dynamic: Record<string, string>,
  defs: LeadPopup7DynamicField[],
): FormValues & { c2?: string; c3?: string; c4?: string; c5?: string } {
  const merged: Record<string, any> = {
    phone: formValues.phone,
    name: formValues.name ?? "",
    email: formValues.email ?? "",
    qualification: formValues.qualification ?? "",
    course: formValues.course ?? "",
    interest: formValues.interest ?? "",
    media: formValues.media ?? "Direct",
    campaign: formValues.campaign ?? "Direct",
  };

  let dynFirst = "";
  let dynLast = "";

  for (const d of defs) {
    if (!d.bind) continue;
    const fromDyn = dynamic[d.id];
    const hasDyn =
      fromDyn !== undefined &&
      fromDyn !== null &&
      String(fromDyn).trim() !== "";
    if (!hasDyn) continue;
    const raw = String(fromDyn).trim();
    if (d.bind === "first_name") {
      dynFirst = raw;
      continue;
    }
    if (d.bind === "last_name") {
      dynLast = raw;
      continue;
    }
    if (LEAD_SCALAR_KEYS.has(d.bind)) {
      merged[d.bind] = raw;
      continue;
    }
    merged[d.bind] = raw;
  }

  const splitName = [dynFirst, dynLast].map((s) => s.trim()).filter(Boolean).join(" ").trim();
  if (splitName) {
    merged.name = splitName;
  }

  /** Course / page mapping uses `erp_unicode`; prefer it over legacy `interest` (e.g. cookie). */
  const courseErp = String(merged.course ?? "").trim();
  const interestErp = String(merged.interest ?? "").trim();
  const areaErp = courseErp || interestErp;
  if (areaErp) {
    merged.course = areaErp;
    merged.interest = areaErp;
  }

  const {
    phone,
    name,
    email,
    qualification,
    course,
    interest,
    media,
    campaign,
    c2,
    c3,
    c4,
    c5,
  } = merged;

  return {
    phone,
    name,
    email,
    qualification,
    course,
    interest,
    media,
    campaign,
    ...(typeof c2 === "string" ? { c2 } : {}),
    ...(typeof c3 === "string" ? { c3 } : {}),
    ...(typeof c4 === "string" ? { c4 } : {}),
    ...(typeof c5 === "string" ? { c5 } : {}),
  };
}
